// A2-582 — pick the country a supply is taxed in (Arts 46, 47, 53, 54(2)(b),
// 55, 56): the work, the service, the property, the event, the handover or
// the underlying supply. Before this only the work location could be picked
// on screen; the rest needed a corrected file. One row, or every ticked row
// at once (an event's tickets share its country); applied by the next re-run.
// The server accepts it
// exactly where it offered it (api/routes_review_resolve_location).
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { authClient } from "../../lib/authApi";
import { errorMessage } from "../../lib/errorMessage";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/sonner";
import { PresetReasonSelect } from "../PresetReasonSelect";
import { LOCATION_COUNTRY_REASONS } from "../../lib/reviewReasonPresets";
import { ALL_COUNTRY_CHOICES, countryOptionLabel } from "../../lib/countries";

const QUESTION = {
  service_country: "Where was the service carried out?",
  property_country: "Where is the property?",
  event_country: "Where did the event take place?",
  handover_country: "Where was it handed over?",
  underlying_country: "Where was the underlying supply made?",
};

export const LocationCountryPicker = ({ tids, field, calcId, calcFrozen, onResolved }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState("");
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const key = tids.length === 1 ? tids[0] : "bulk";
  const question = t(`ui.review_answers.location.${field}`, { defaultValue: QUESTION[field] || QUESTION.service_country });
  const valid = /^[A-Z]{2}$/.test(country) && reason.trim().length >= 8;
  const reset = () => { setCountry(""); setReason(""); setPreset(""); setErr(null); };

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      await authClient.post(`/runs/calc/${calcId}/resolve-location-country`,
        { txn_ids: tids, field, country, reason: reason.trim() });
      setOpen(false); reset();
      toast.success(t("ui.review_answers.location_saved", { n: tids.length, defaultValue: "Country recorded on {{n}} row(s) — figures change at the next re-run" }));
      // A1-105 — saved for the one re-run, like every Step 4 answer
      if (onResolved) await onResolved();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (calcFrozen || !tids.length) return null;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`loc-open-${field}-${key}`}
                className="text-xs px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800">
          {tids.length > 1
            ? t("ui.review_answers.location_open_many", { n: tids.length, defaultValue: "Set country for {{n}} rows →" })
            : t("ui.review_answers.location_open", { defaultValue: "Set country →" })}
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid={`loc-popover-${field}-${key}`}
                      className="w-80 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 space-y-2">
        <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">{question}</div>
        <select data-testid={`loc-country-${field}-${key}`} value={country} aria-label={question}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded px-2 py-1 text-xs">
          <option value="">{t("ui.review_answers.location_pick", { defaultValue: "Pick a country…" })}</option>
          {ALL_COUNTRY_CHOICES.map((c) => <option key={c} value={c}>{countryOptionLabel(c)}</option>)}
        </select>
        <PresetReasonSelect testid={`loc-reason-${field}-${key}`}
                            presets={LOCATION_COUNTRY_REASONS[field] || LOCATION_COUNTRY_REASONS.service_country}
                            preset={preset} onPick={(p, v) => { setPreset(p); setReason(v); }} />
        {err && <div data-testid={`loc-error-${field}-${key}`} className="text-xs text-red-700 dark:text-red-300">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => { setOpen(false); reset(); }}
                  className="text-xs underline text-slate-600 dark:text-slate-300">
            {t("ui.review_grouped.sc_cancel", { defaultValue: "Cancel" })}
          </button>
          <button type="button" data-testid={`loc-submit-${field}-${key}`} disabled={busy || !valid} onClick={submit}
                  data-resolution-class="self-resolve"
                  data-resolution-endpoint="POST /api/runs/calc/{calc_id}/resolve-location-country"
                  className="text-xs font-semibold px-3 py-1 rounded bg-teal-700 hover:bg-teal-800 disabled:opacity-50 text-white">
            {busy ? t("ui.review_grouped.sc_submitting", { defaultValue: "Saving…" })
                  : t("ui.review_grouped.sc_submit", { defaultValue: "Save" })}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default LocationCountryPicker;
